// src/pages/WelcomePage/WelcomePage.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { getGreeting } from '../../utils/avatarHelpers';
import { CONTAINER_PADDING, CONTAINER_PADDING_SMALL } from '../../utils/constants';

const Container = styled.div`
  /* ✅ КРИТИЧНО для iOS: используем динамический viewport вместо 100vh */
  min-height: var(--tg-viewport-stable-height, 100vh);
  padding: 40px 20px ${CONTAINER_PADDING.bottom}px;
  max-width: 430px;
  margin: 0 auto;
  background:
    radial-gradient(circle at 20% 20%, rgba(123, 104, 238, 0.12) 0%, transparent 60%),
    radial-gradient(circle at 80% 80%, rgba(135, 206, 235, 0.12) 0%, transparent 60%),
    var(--bg-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 375px) {
    padding: 32px 16px ${CONTAINER_PADDING_SMALL.bottom}px;
  }

  [data-theme="dark"] & {
    background:
      radial-gradient(circle at 20% 20%, rgba(157, 142, 238, 0.15) 0%, transparent 60%),
      radial-gradient(circle at 80% 80%, rgba(160, 216, 240, 0.15) 0%, transparent 60%),
      var(--bg-primary);
  }
`;

const ContentCard = styled(motion.div)`
  background: var(--card-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-medium);
  border-radius: 32px;
  padding: 48px 32px;
  box-shadow:
    0 20px 60px rgba(123, 104, 238, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  position: relative;
  overflow: hidden;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB, #9966CC);
    background-size: 200% 100%;
    animation: gradientShift 3s ease infinite;
  }

  @keyframes gradientShift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  @media (max-width: 375px) {
    padding: 40px 24px;
  }

  [data-theme="dark"] & {
    box-shadow:
      0 20px 60px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);

    &::before {
      background: linear-gradient(90deg, #9d8eee, #a0d8f0, #b896dc);
    }
  }
`;

const IconWrapper = styled(motion.div)`
  width: 120px;
  height: 120px;
  margin: 0 auto 32px;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  border-radius: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 64px;
  box-shadow: 
    0 12px 32px rgba(123, 104, 238, 0.4),
    inset 0 2px 0 rgba(255, 255, 255, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 36px;
    padding: 2px;
    background: linear-gradient(135deg, #7B68EE, #87CEEB);
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0.5;
  }
  
  @media (max-width: 375px) {
    width: 100px;
    height: 100px;
    font-size: 56px;
  }
`;

const Greeting = styled(motion.h1)`
  font-size: 28px;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 12px 0;
  line-height: 1.2;

  @media (max-width: 375px) {
    font-size: 24px;
  }
`;

const BrandTitle = styled(motion.h2)`
  font-size: 36px;
  font-weight: 900;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 8px 0;
  letter-spacing: -1px;
  
  @media (max-width: 375px) {
    font-size: 32px;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 18px;
  font-weight: 600;
  color: #7B68EE;
  margin: 0 0 24px 0;

  @media (max-width: 375px) {
    font-size: 16px;
  }

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

const Description = styled(motion.p)`
  font-size: 15px;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0 0 32px 0;
  max-width: 350px;

  @media (max-width: 375px) {
    font-size: 14px;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const PrimaryButton = styled(motion.button)`
  width: 100%;
  padding: 18px 24px;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(123, 104, 238, 0.4);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(123, 104, 238, 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 375px) {
    padding: 16px 20px;
    font-size: 16px;
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg, #9d8eee, #b896dc);
    box-shadow: 0 8px 24px rgba(157, 142, 238, 0.5);

    &:hover {
      box-shadow: 0 12px 32px rgba(157, 142, 238, 0.6);
    }
  }
`;

/* Оставляем SecondaryButton в коде на будущее, но не используем его.
const SecondaryButton = styled(motion.button)`
  width: 100%;
  padding: 18px 24px;
  background: transparent;
  color: #7B68EE;
  border: 2px solid rgba(123, 104, 238, 0.3);
  border-radius: 20px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(123, 104, 238, 0.08);
    border-color: rgba(123, 104, 238, 0.5);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 375px) {
    padding: 16px 20px;
    font-size: 15px;
  }
`;
*/

const FeaturesList = styled(motion.div)`
  display: grid;
  gap: 12px;
  margin: 24px 0 32px;
  text-align: left;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: var(--surface-light);
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: var(--surface-medium);
    transform: translateX(4px);
  }
`;

const FeatureIcon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
`;

const FeatureText = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
`;

const FeatureDescription = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.4;
`;

/* ==== Новый инфоблок-аккордеон вместо "Пропустить" ==== */
const InfoBlock = styled(motion.div)`
  margin: 24px 0;
  padding: 20px;
  background: var(--surface-medium);
  border-radius: 16px;
  border-left: 4px solid #7B68EE;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(123, 104, 238, 0.12);
    transform: translateX(2px);
  }

  [data-theme="dark"] & {
    border-left-color: #C4B5FD;

    &:hover {
      background: rgba(157, 142, 238, 0.2);
    }
  }
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const InfoTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #7B68EE;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

const InfoToggle = styled.span<{ $isOpen: boolean }>`
  font-size: 20px;
  transition: transform 0.3s ease;
  transform: ${props => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`;

const InfoContent = styled(motion.div)`
  overflow: hidden;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
`;

export const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, hapticFeedback } = useTelegramWebApp();
  const[isInfoOpen, setIsInfoOpen] = useState(false);

  const handleStart = () => {
    hapticFeedback('success');
    navigate('/questionnaire');
  };

  const greeting = getGreeting(user?.first_name);

  return (
    <Container>
      <ContentCard
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <IconWrapper
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2
          }}
        >
          ✨
        </IconWrapper>

        <Greeting
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {greeting}
        </Greeting>

        <BrandTitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          GELTEK
        </BrandTitle>

        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          Календарь применения ретинола
        </Subtitle>

        <Description
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          Персональный подход к уходу за кожей с учётом особенностей Вашей кожи.
        </Description>

        <FeaturesList
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
        >
          <FeatureItem>
            <FeatureIcon>📅</FeatureIcon>
            <FeatureText>
              <FeatureTitle>Персональный график</FeatureTitle>
              <FeatureDescription>
                План применения адаптирован под твою кожу
              </FeatureDescription>
            </FeatureText>
          </FeatureItem>

          <FeatureItem>
            <FeatureIcon>🏆</FeatureIcon>
            <FeatureText>
              <FeatureTitle>Система достижений</FeatureTitle>
              <FeatureDescription>
                Мотивация для регулярного ухода
              </FeatureDescription>
            </FeatureText>
          </FeatureItem>

          <FeatureItem>
            <FeatureIcon>💜</FeatureIcon>
            <FeatureText>
              <FeatureTitle>Поддержка 24/7</FeatureTitle>
              <FeatureDescription>
                Специалисты всегда на связи
              </FeatureDescription>
            </FeatureText>
          </FeatureItem>
        </FeaturesList>

        {/* Инфоблок-аккордеон вместо кнопки "Пропустить" */}
        <InfoBlock
          onClick={() => {
            hapticFeedback('light');
            setIsInfoOpen(!isInfoOpen);
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.75, duration: 0.5 }}
        >
          <InfoHeader>
            <InfoTitle>
              <span>📝</span>
              <span>Зачем нужна анкета?</span>
            </InfoTitle>
            <InfoToggle $isOpen={isInfoOpen}>▼</InfoToggle>
          </InfoHeader>

          <AnimatePresence initial={false}>
            {isInfoOpen && (
              <InfoContent
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <InfoText>
                  💜 Анкета помогает подобрать персональный план применения
                  ретинола с учётом типа кожи, чувствительности и Вашего опыта.
                  <br /><br />
                  ⏱️ Это занимает всего 2 минуты и помогает достичь лучших результатов от применения ретинола. ✨
                </InfoText>
              </InfoContent>
            )}
          </AnimatePresence>
        </InfoBlock>

        <ButtonGroup
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <PrimaryButton
            onClick={handleStart}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span>✨</span>
            <span>Начать</span>
          </PrimaryButton>

          {/* Кнопка "Пропустить" удалена */}
        </ButtonGroup>
      </ContentCard>
    </Container>
  );
};
